import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Typography } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Storage as ServerIcon,
  Security as ShieldIcon,
  Warning as AlertTriangleIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowRightIcon,
  Business as BuildingIcon,
  Wifi as WifiIcon,
  Phone as PhoneIcon,
  Notifications as BellIcon,
  Lock as LockIcon,
  Group as UsersIcon,
  Smartphone as SmartphoneIcon,
  LocationOn as MapPinIcon,
  Videocam as CameraIcon,
  ConfirmationNumber as TicketIcon,
  Settings as SettingsIcon,
  Monitor as MonitorIcon,
  Bolt as ZapIcon,
  Public as GlobeIcon,
  Favorite as HeartIcon,
  PlayArrow as PlayIcon,
  SettingsInputAntenna as RadioIcon,
  VpnKey as KeyIcon,
  Description as FileTextIcon,
  Dashboard as LayoutIcon,
  TrackChanges as TargetIcon,
  RocketLaunch as RocketIcon,
  Home as HomeIcon,
  Work as BriefcaseIcon,
  Apartment as BuildingAltIcon,
  OpenInNew as OpenInNewIcon,
  Lightbulb as LightbulbIcon,
  Star as StarIcon,
  IntegrationInstructions as IntegrationIcon,
  Euro as EuroIcon,
  NightShelter as ShelterIcon,
  CloudDone as CloudIcon,
  Sensors as SensorIcon,
  MonitorHeart as HeartbeatIcon,
  Medication as PillIcon,
  AccountBalance as GovernmentIcon,
  PhoneAndroid as MobileIcon,
  CalendarMonth as CalendarIcon,
  Forum as MessageCircleIcon,
  HourglassEmpty as HourglassIcon,
} from '@mui/icons-material';
import usePermission from '../../hooks/usePermission';

// CARO Brand Colors
const colors = {
  plum: {
    lighter: '#E7D6E0',
    light: '#C87DA1',
    main: '#9A3F69',
    dark: '#702C4D',
    darker: '#4A1C33',
  },
  gold: {
    lighter: '#F6EDD0',
    light: '#E7C87B',
    main: '#DAA03D',
    dark: '#B5842F',
    darker: '#78561F',
  }
};

// Demo link handler
const openDemo = () => {
  window.open('https://portal.caro.care', '_blank');
};

// Section Navigation Component
const SectionNav = ({ currentSection, totalSections, onNavigate, sectionTitles }) => (
  <Box
    sx={{
      position: 'fixed',
      bottom: 32,
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      zIndex: 50,
    }}
  >
    <IconButton
      onClick={() => onNavigate(currentSection - 1)}
      disabled={currentSection === 0}
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: 'rgba(255,255,255,0.1)',
        backdropFilter: 'blur(8px)',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
        '&:disabled': { opacity: 0.3 },
        color: 'white',
      }}
    >
      <ChevronLeftIcon />
    </IconButton>

    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {Array.from({ length: totalSections }).map((_, i) => (
        <Box
          key={i}
          onClick={() => onNavigate(i)}
          sx={{
            width: i === currentSection ? 'auto' : 12,
            height: 12,
            px: i === currentSection ? 2 : 0,
            borderRadius: i === currentSection ? 2 : '50%',
            bgcolor: i === currentSection ? 'white' : 'rgba(255,255,255,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
          }}
        >
          {i === currentSection && (
            <Typography sx={{ fontSize: '0.7rem', color: 'black', fontWeight: 600 }}>
              {sectionTitles[i]}
            </Typography>
          )}
        </Box>
      ))}
    </Box>

    <IconButton
      onClick={() => onNavigate(currentSection + 1)}
      disabled={currentSection === totalSections - 1}
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: 'rgba(255,255,255,0.1)',
        backdropFilter: 'blur(8px)',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
        '&:disabled': { opacity: 0.3 },
        color: 'white',
      }}
    >
      <ChevronRightIcon />
    </IconButton>
  </Box>
);

// Animated Background Component
const AnimatedBackground = ({ variant = 'default' }) => {
  const getGradient = () => {
    switch (variant) {
      case 'danger':
        return 'linear-gradient(135deg, #7f1d1d 0%, #111827 50%, #030712 100%)';
      case 'success':
        return `linear-gradient(135deg, ${colors.plum.darker} 0%, ${colors.plum.dark} 30%, #111827 100%)`;
      case 'gold':
        return `linear-gradient(135deg, ${colors.gold.darker} 0%, #111827 50%, #030712 100%)`;
      default:
        return 'linear-gradient(135deg, #111827 0%, #030712 50%, #000 100%)';
    }
  };

  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', inset: 0, background: getGradient() }} />
      <Box
        sx={{
          position: 'absolute',
          top: '25%',
          left: '25%',
          width: 384,
          height: 384,
          borderRadius: '50%',
          opacity: 0.2,
          filter: 'blur(48px)',
          background: colors.plum.main,
          animation: 'bgPulse 4s ease-in-out infinite',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '25%',
          right: '25%',
          width: 320,
          height: 320,
          borderRadius: '50%',
          opacity: 0.15,
          filter: 'blur(48px)',
          background: colors.gold.main,
          animation: 'bgPulse 4s ease-in-out infinite',
          animationDelay: '1s',
        }}
      />
    </Box>
  );
};

// Section 1: De Oude Situatie (with animated transformation)
const OldSystemSection = ({ isActive }) => {
  const [phase, setPhase] = useState(0);
  const svgRef = useRef(null);

  useEffect(() => {
    if (isActive) {
      setPhase(0);
      const timers = [
        setTimeout(() => setPhase(1), 500),
        setTimeout(() => setPhase(2), 1500),
        setTimeout(() => setPhase(3), 2500),
      ];
      return () => timers.forEach(t => clearTimeout(t));
    }
    setPhase(0);
  }, [isActive]);

  const sites = [
    { id: 'A', x: 100 },
    { id: 'B', x: 300 },
    { id: 'C', x: 500 },
    { id: 'D', x: 700 },
  ];
  const centralX = 400;
  const siteY = 80;
  const serverY = 280;
  const wgkY = 420;

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        opacity: isActive ? 1 : 0,
        pointerEvents: isActive ? 'auto' : 'none',
        transition: 'opacity 0.7s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <AnimatedBackground variant="danger" />

      <Box sx={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 900, px: 4 }}>
        <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: 'white', textAlign: 'center', mb: 1 }}>
          De <span style={{ color: '#f87171' }}>Oude</span> Situatie
        </Typography>
        <Typography sx={{ fontSize: '1.1rem', color: '#9ca3af', textAlign: 'center', mb: 4 }}>
          Single Point of Failure Architectuur
        </Typography>

        {/* SVG Architecture Diagram */}
        <Box sx={{ position: 'relative', width: '100%', height: 500 }}>
          <svg
            ref={svgRef}
            width="100%"
            height="100%"
            viewBox="0 0 800 500"
            style={{ position: 'absolute', top: 0, left: 0 }}
          >
            {/* Connection lines from sites to Prinsenhof */}
            {sites.map((site, i) => (
              <line
                key={`line-${site.id}`}
                x1={site.x}
                y1={siteY + 50}
                x2={centralX}
                y2={serverY - 40}
                stroke="#ef4444"
                strokeWidth="2"
                strokeDasharray="8,4"
                opacity={phase >= 1 ? 0.7 : 0}
                style={{
                  transition: 'opacity 0.5s ease',
                  transitionDelay: `${i * 150}ms`,
                }}
              />
            ))}

            {/* Connection from Prinsenhof to WGK */}
            <line
              x1={centralX}
              y1={serverY + 50}
              x2={centralX}
              y2={wgkY - 25}
              stroke="#ef4444"
              strokeWidth="2"
              strokeDasharray="8,4"
              opacity={phase >= 2 ? 0.7 : 0}
              style={{ transition: 'opacity 0.5s ease' }}
            />
          </svg>

          {/* Sites */}
          {sites.map((site, i) => (
            <Box
              key={site.id}
              sx={{
                position: 'absolute',
                left: `calc(${(site.x / 800) * 100}% - 45px)`,
                top: siteY - 40,
                width: 90,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                opacity: phase >= 1 ? 1 : 0,
                transform: phase >= 1 ? 'translateY(0)' : 'translateY(-20px)',
                transition: 'all 0.5s ease',
                transitionDelay: `${i * 100}ms`,
              }}
            >
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: 2,
                  bgcolor: 'rgba(31, 41, 55, 0.9)',
                  border: '1px solid #4b5563',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BuildingIcon sx={{ width: 24, height: 24, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: '0.65rem', color: '#6b7280', mt: 0.25 }}>Site {site.id}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.25, mt: 0.5 }}>
                <PhoneIcon sx={{ width: 12, height: 12, color: '#6b7280' }} />
                <SettingsIcon sx={{ width: 12, height: 12, color: '#6b7280' }} />
                <BellIcon sx={{ width: 12, height: 12, color: '#6b7280' }} />
              </Box>
            </Box>
          ))}

          {/* Prinsenhof Server - Bottleneck */}
          <Box
            sx={{
              position: 'absolute',
              left: `calc(${(centralX / 800) * 100}% - 90px)`,
              top: serverY - 50,
              opacity: phase >= 2 ? 1 : 0,
              transform: phase >= 2 ? 'scale(1)' : 'scale(0.8)',
              transition: 'all 0.5s ease',
            }}
          >
            <Box
              sx={{
                width: 180,
                height: 100,
                borderRadius: 2,
                bgcolor: 'rgba(127, 29, 29, 0.6)',
                border: '2px solid rgba(239, 68, 68, 0.6)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                animation: phase >= 2 ? 'dangerPulse 2s infinite' : 'none',
              }}
            >
              <ServerIcon sx={{ width: 32, height: 32, color: '#f87171' }} />
              <Typography sx={{ color: '#fca5a5', fontWeight: 600, fontSize: '0.9rem', mt: 0.5 }}>Prinsenhof</Typography>
              <Typography sx={{ color: 'rgba(248, 113, 113, 0.7)', fontSize: '0.7rem' }}>Centrale Server</Typography>
            </Box>

            {/* Risk Labels */}
            {phase >= 3 && (
              <>
                <Box sx={{ position: 'absolute', top: -30, left: -60, display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, bgcolor: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.5)', borderRadius: 9999 }}>
                  <AlertTriangleIcon sx={{ width: 12, height: 12, color: '#f87171' }} />
                  <Typography sx={{ color: '#fca5a5', fontSize: '0.7rem' }}>Bottleneck</Typography>
                </Box>
                <Box sx={{ position: 'absolute', top: -30, right: -70, display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, bgcolor: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.5)', borderRadius: 9999 }}>
                  <AlertTriangleIcon sx={{ width: 12, height: 12, color: '#f87171' }} />
                  <Typography sx={{ color: '#fca5a5', fontSize: '0.7rem' }}>Oude servers</Typography>
                </Box>
                <Box sx={{ position: 'absolute', bottom: -30, left: -80, display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, bgcolor: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.5)', borderRadius: 9999 }}>
                  <AlertTriangleIcon sx={{ width: 12, height: 12, color: '#f87171' }} />
                  <Typography sx={{ color: '#fca5a5', fontSize: '0.7rem' }}>Publiek internet</Typography>
                </Box>
                <Box sx={{ position: 'absolute', bottom: -30, right: -60, display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, bgcolor: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.5)', borderRadius: 9999 }}>
                  <AlertTriangleIcon sx={{ width: 12, height: 12, color: '#f87171' }} />
                  <Typography sx={{ color: '#fca5a5', fontSize: '0.7rem' }}>Single point</Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Wit Gele Kruis */}
          <Box
            sx={{
              position: 'absolute',
              left: `calc(${(centralX / 800) * 100}% - 80px)`,
              top: wgkY - 25,
              opacity: phase >= 2 ? 1 : 0,
              transition: 'all 0.5s ease',
              transitionDelay: '300ms',
            }}
          >
            <Box
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                bgcolor: 'rgba(120, 86, 31, 0.3)',
                border: '1px solid rgba(202, 138, 4, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <HeartIcon sx={{ width: 20, height: 20, color: '#eab308' }} />
              <Typography sx={{ color: '#facc15', fontWeight: 500, fontSize: '0.9rem' }}>Wit Gele Kruis</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Section 2: De Transformatie (animated comparison)
const TransformationSection = ({ isActive }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (isActive) {
      setPhase(0);
      const timers = [
        setTimeout(() => setPhase(1), 400),
        setTimeout(() => setPhase(2), 1200),
        setTimeout(() => setPhase(3), 2000),
        setTimeout(() => setPhase(4), 2800),
      ];
      return () => timers.forEach(t => clearTimeout(t));
    }
    setPhase(0);
  }, [isActive]);

  const sites = [
    { id: 'A', x: 100 },
    { id: 'B', x: 300 },
    { id: 'C', x: 500 },
    { id: 'D', x: 700 },
  ];
  const centralX = 400;
  const siteY = 80;
  const dcY = 280;
  const wgkY = 480;

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        opacity: isActive ? 1 : 0,
        pointerEvents: isActive ? 'auto' : 'none',
        transition: 'opacity 0.7s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // Define all keyframe animations
        '@keyframes flowDown': {
          '0%': { strokeDashoffset: 200 },
          '100%': { strokeDashoffset: 0 },
        },
        '@keyframes flowUp': {
          '0%': { strokeDashoffset: -200 },
          '100%': { strokeDashoffset: 0 },
        },
        '@keyframes pulseGlow': {
          '0%, 100%': { boxShadow: '0 0 20px rgba(34, 197, 94, 0.3), 0 0 40px rgba(154, 63, 105, 0.2)' },
          '50%': { boxShadow: '0 0 30px rgba(34, 197, 94, 0.5), 0 0 60px rgba(154, 63, 105, 0.4)' },
        },
        '@keyframes dataPulse': {
          '0%, 100%': { opacity: 0.3, transform: 'scale(1)' },
          '50%': { opacity: 1, transform: 'scale(1.2)' },
        },
        '@keyframes mirrorSync': {
          '0%': { opacity: 0.4, transform: 'scaleX(0.3)', transformOrigin: 'left' },
          '50%': { opacity: 1, transform: 'scaleX(1)', transformOrigin: 'left' },
          '51%': { opacity: 1, transform: 'scaleX(1)', transformOrigin: 'right' },
          '100%': { opacity: 0.4, transform: 'scaleX(0.3)', transformOrigin: 'right' },
        },
        '@keyframes slideInFromTop': {
          '0%': { opacity: 0, transform: 'translateY(-30px) scale(0.9)' },
          '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
        },
        '@keyframes slideInFromBottom': {
          '0%': { opacity: 0, transform: 'translateY(30px) scale(0.9)' },
          '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
        },
        '@keyframes scaleIn': {
          '0%': { opacity: 0, transform: 'translateX(-50%) scale(0.8)' },
          '100%': { opacity: 1, transform: 'translateX(-50%) scale(1)' },
        },
        '@keyframes shimmer': {
          '0%': { backgroundPosition: '-200% 0' },
          '100%': { backgroundPosition: '200% 0' },
        },
        '@keyframes floatBadge': {
          '0%, 100%': { transform: 'translateX(-50%) translateY(0)' },
          '50%': { transform: 'translateX(-50%) translateY(-3px)' },
        },
      }}
    >
      <AnimatedBackground variant="success" />

      <Box sx={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 900, px: 4 }}>
        {/* Animated Title */}
        <Typography
          sx={{
            fontSize: '2.5rem',
            fontWeight: 700,
            color: 'white',
            textAlign: 'center',
            mb: 1,
            opacity: phase >= 0 ? 1 : 0,
            transform: phase >= 0 ? 'translateY(0)' : 'translateY(-20px)',
            transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          De <span style={{
            color: colors.gold.main,
            textShadow: `0 0 20px ${colors.gold.main}60`,
          }}>Transformatie</span>
        </Typography>
        <Typography
          sx={{
            fontSize: '1.1rem',
            color: '#9ca3af',
            textAlign: 'center',
            mb: 4,
            opacity: phase >= 0 ? 1 : 0,
            transform: phase >= 0 ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'all 0.6s ease 0.2s',
          }}
        >
          Robuust, Redundant & Betrouwbaar
        </Typography>

        {/* SVG Architecture Diagram */}
        <Box sx={{ position: 'relative', width: '100%', height: 550 }}>
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 800 570"
            style={{ position: 'absolute', top: 0, left: 0 }}
          >
            <defs>
              <linearGradient id="lineGradientNew" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={colors.plum.light} />
                <stop offset="50%" stopColor={colors.gold.main} />
                <stop offset="100%" stopColor={colors.gold.light} />
              </linearGradient>
              <linearGradient id="endpointGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={colors.gold.main} />
                <stop offset="100%" stopColor={colors.gold.light} />
              </linearGradient>
              <filter id="glowFilter" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Connection lines from sites to Datacenter - with animated flow */}
            {sites.map((site, i) => (
              <g key={`line-group-${site.id}`}>
                {/* Background glow line */}
                <path
                  d={`M ${site.x} ${siteY + 55} Q ${site.x} ${dcY - 60} ${centralX} ${dcY - 40}`}
                  fill="none"
                  stroke={colors.plum.light}
                  strokeWidth="8"
                  opacity={phase >= 2 ? 0.15 : 0}
                  filter="url(#glowFilter)"
                  style={{
                    transition: 'opacity 0.8s ease',
                    transitionDelay: `${i * 100}ms`,
                  }}
                />
                {/* Main line */}
                <path
                  d={`M ${site.x} ${siteY + 55} Q ${site.x} ${dcY - 60} ${centralX} ${dcY - 40}`}
                  fill="none"
                  stroke="url(#lineGradientNew)"
                  strokeWidth="3"
                  strokeDasharray="200"
                  opacity={phase >= 2 ? 1 : 0}
                  style={{
                    transition: 'opacity 0.3s ease',
                    transitionDelay: `${i * 100}ms`,
                    animation: phase >= 2 ? `flowDown 1.5s ease-out ${i * 0.15}s forwards` : 'none',
                  }}
                />
              </g>
            ))}

            {/* Connection from Datacenter to endpoints - with animated flow */}
            {[200, 400, 600].map((endX, i) => (
              <g key={`endpoint-line-group-${i}`}>
                {/* Background glow line */}
                <path
                  d={`M ${centralX} ${dcY + 70} Q ${centralX} ${wgkY - 60} ${endX} ${wgkY - 25}`}
                  fill="none"
                  stroke={colors.gold.main}
                  strokeWidth="8"
                  opacity={phase >= 4 ? 0.2 : 0}
                  filter="url(#glowFilter)"
                  style={{
                    transition: 'opacity 0.8s ease',
                    transitionDelay: `${i * 100}ms`,
                  }}
                />
                {/* Main line */}
                <path
                  d={`M ${centralX} ${dcY + 70} Q ${centralX} ${wgkY - 60} ${endX} ${wgkY - 25}`}
                  fill="none"
                  stroke="url(#endpointGradient)"
                  strokeWidth="3"
                  strokeDasharray="200"
                  opacity={phase >= 4 ? 1 : 0}
                  style={{
                    transition: 'opacity 0.3s ease',
                    transitionDelay: `${i * 100}ms`,
                    animation: phase >= 4 ? `flowDown 1.2s ease-out ${i * 0.1}s forwards` : 'none',
                  }}
                />
              </g>
            ))}
          </svg>

          {/* Sites with local capabilities - enhanced */}
          {sites.map((site, i) => (
            <Box
              key={site.id}
              sx={{
                position: 'absolute',
                left: `calc(${(site.x / 800) * 100}% - 50px)`,
                top: siteY - 40,
                width: 100,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                opacity: phase >= 1 ? 1 : 0,
                animation: phase >= 1 ? `slideInFromTop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 0.1}s forwards` : 'none',
              }}
            >
              <Box
                sx={{
                  width: 85,
                  height: 85,
                  borderRadius: 3,
                  bgcolor: 'rgba(31, 41, 55, 0.95)',
                  border: '1px solid rgba(75, 85, 99, 0.6)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 30px rgba(0, 0, 0, 0.4), 0 0 20px ${colors.plum.main}30`,
                    borderColor: colors.plum.light,
                  },
                }}
              >
                <BuildingIcon sx={{ width: 26, height: 26, color: '#e5e7eb' }} />
                <Typography sx={{ fontSize: '0.7rem', color: '#d1d5db', mt: 0.5, fontWeight: 500 }}>Site {site.id}</Typography>

                {/* Local capabilities badges - animated */}
                <Box sx={{ position: 'absolute', bottom: -12, display: 'flex', gap: 0.5 }}>
                  {[PhoneIcon, SettingsIcon].map((IconComp, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        bgcolor: 'rgba(34, 197, 94, 0.25)',
                        border: '1px solid rgba(34, 197, 94, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)',
                      }}
                    >
                      <IconComp sx={{ width: 11, height: 11, color: '#4ade80' }} />
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 2,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.2)',
                }}
              >
                <Typography sx={{ fontSize: '0.6rem', color: '#9ca3af', fontWeight: 500 }}>MQTT + API</Typography>
                <WifiIcon sx={{ width: 10, height: 10, color: '#4ade80' }} />
              </Box>
            </Box>
          ))}

          {/* Secure connection indicator - enhanced */}
          {phase >= 2 && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: dcY - 80,
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 2,
                py: 0.75,
                borderRadius: 9999,
                bgcolor: 'rgba(34, 197, 94, 0.15)',
                border: '1px solid rgba(34, 197, 94, 0.4)',
                boxShadow: '0 4px 15px rgba(34, 197, 94, 0.2)',
                animation: 'scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
              }}
            >
              <LockIcon sx={{ width: 14, height: 14, color: '#4ade80' }} />
              <Typography sx={{ fontSize: '0.75rem', color: '#4ade80', fontWeight: 500 }}>Beveiligde verbinding</Typography>
            </Box>
          )}

          {/* Datacenter - Dual Servers - enhanced with glow */}
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: dcY - 45,
              transform: 'translateX(-50%)',
              opacity: phase >= 3 ? 1 : 0,
              animation: phase >= 3 ? 'scaleIn 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' : 'none',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                p: 3,
                borderRadius: 4,
                border: `2px solid ${colors.plum.light}70`,
                background: `linear-gradient(145deg, ${colors.plum.darker}70, ${colors.plum.dark}40)`,
                boxShadow: phase >= 3 ? `0 8px 32px rgba(0, 0, 0, 0.4), 0 0 40px ${colors.plum.main}30` : 'none',
              }}
            >
              {/* Datacenter label - enhanced */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -14,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  px: 2.5,
                  py: 0.75,
                  borderRadius: 9999,
                  background: `linear-gradient(135deg, ${colors.plum.main}, ${colors.plum.dark})`,
                  color: 'white',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  boxShadow: `0 4px 15px ${colors.plum.main}50`,
                }}
              >
                DATACENTER
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                {/* Server 1 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 65,
                      height: 65,
                      borderRadius: 3,
                      bgcolor: 'rgba(34, 197, 94, 0.2)',
                      border: '2px solid rgba(34, 197, 94, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 15px rgba(34, 197, 94, 0.2), inset 0 0 20px rgba(34, 197, 94, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 6px 20px rgba(34, 197, 94, 0.3)',
                      },
                    }}
                  >
                    <ServerIcon sx={{ width: 28, height: 28, color: '#4ade80' }} />
                  </Box>
                  <Typography sx={{ fontSize: '0.7rem', color: '#d1d5db', fontWeight: 500 }}>Server 1</Typography>
                </Box>

                {/* Mirror connection - animated */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {[0, 1].map((idx) => (
                      <Box
                        key={idx}
                        sx={{
                          width: 28,
                          height: 5,
                          borderRadius: 2,
                          bgcolor: '#4ade80',
                          boxShadow: '0 0 10px rgba(34, 197, 94, 0.6)',
                          animation: phase >= 3 ? `mirrorSync 1.8s ease-in-out ${idx * 0.3}s infinite` : 'none',
                        }}
                      />
                    ))}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      color: '#4ade80',
                      fontWeight: 700,
                      textShadow: '0 0 12px rgba(34, 197, 94, 0.6)',
                      letterSpacing: '0.05em',
                    }}
                  >
                    MIRROR
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {[0, 1].map((idx) => (
                      <Box
                        key={idx}
                        sx={{
                          width: 28,
                          height: 5,
                          borderRadius: 2,
                          bgcolor: '#4ade80',
                          boxShadow: '0 0 10px rgba(34, 197, 94, 0.6)',
                          animation: phase >= 3 ? `mirrorSync 1.8s ease-in-out ${idx * 0.3 + 0.9}s infinite` : 'none',
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Server 2 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 65,
                      height: 65,
                      borderRadius: 3,
                      bgcolor: 'rgba(34, 197, 94, 0.2)',
                      border: '2px solid rgba(34, 197, 94, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 15px rgba(34, 197, 94, 0.2), inset 0 0 20px rgba(34, 197, 94, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 6px 20px rgba(34, 197, 94, 0.3)',
                      },
                    }}
                  >
                    <ServerIcon sx={{ width: 28, height: 28, color: '#4ade80' }} />
                  </Box>
                  <Typography sx={{ fontSize: '0.7rem', color: '#d1d5db', fontWeight: 500 }}>Server 2</Typography>
                </Box>
              </Box>

              {/* Feature badges - enhanced */}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                {[
                  { icon: ShieldIcon, label: 'Redundantie', color: '#4ade80' },
                  { icon: ZapIcon, label: 'Retry Logic', color: '#facc15' },
                ].map((feature, idx) => (
                  <Box
                    key={feature.label}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      bgcolor: `${feature.color}15`,
                      border: `1px solid ${feature.color}40`,
                    }}
                  >
                    <feature.icon sx={{ width: 14, height: 14, color: feature.color }} />
                    <Typography sx={{ fontSize: '0.7rem', color: '#d1d5db', fontWeight: 500 }}>{feature.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Endpoints: Mantelzorgers, Wit Gele Kruis, Others - enhanced */}
          {[
            { x: 200, name: 'Mantelzorgers', Icon: UsersIcon, color: colors.plum.light, completed: true },
            { x: 400, name: 'Wit Gele Kruis', Icon: HeartIcon, color: colors.gold.main, completed: true },
            { x: 600, name: 'Andere', Icon: GlobeIcon, color: '#9ca3af', completed: false },
          ].map((endpoint, i) => {
            const EndpointIcon = endpoint.Icon;
            return (
              <Box
                key={endpoint.name}
                sx={{
                  position: 'absolute',
                  left: `calc(${(endpoint.x / 800) * 100}% - 75px)`,
                  top: wgkY - 25,
                  width: 150,
                  opacity: phase >= 4 ? 1 : 0,
                  animation: phase >= 4 ? `slideInFromBottom 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 0.12}s forwards` : 'none',
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 3,
                    bgcolor: endpoint.completed ? `${colors.gold.darker}25` : 'rgba(75, 85, 99, 0.3)',
                    border: `1px solid ${endpoint.color}60`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    boxShadow: endpoint.completed
                      ? `0 4px 20px ${endpoint.color}20`
                      : '0 4px 15px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: `0 8px 25px ${endpoint.color}30`,
                    },
                  }}
                >
                  <EndpointIcon sx={{ width: 20, height: 20, color: endpoint.color }} />
                  <Typography sx={{ color: endpoint.color, fontWeight: 600, fontSize: '0.8rem' }}>{endpoint.name}</Typography>
                  {endpoint.completed ? (
                    <CheckCircleIcon sx={{ width: 16, height: 16, color: '#4ade80' }} />
                  ) : (
                    <HourglassIcon sx={{ width: 16, height: 16, color: '#facc15' }} />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

// Section 3: Het Ecosysteem - Centered around CARO Datacenter
const EcosystemSection = ({ isActive }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (isActive) {
      setPhase(0);
      const timers = [
        setTimeout(() => setPhase(1), 300),
        setTimeout(() => setPhase(2), 800),
        setTimeout(() => setPhase(3), 1400),
      ];
      return () => timers.forEach(t => clearTimeout(t));
    }
    setPhase(0);
  }, [isActive]);

  const services = [
    { id: 'api', name: 'api.caro.care', Icon: GlobeIcon, desc: 'REST API' },
    { id: 'mqtt', name: 'mqtt.caro.care', Icon: RadioIcon, desc: 'IoT Protocol' },
    { id: 'ac', name: 'ac.caro.care', Icon: PhoneIcon, desc: 'Videophones' },
    { id: 'sv', name: 'sv.caro.care', Icon: KeyIcon, desc: 'Toegangscontrole' },
    { id: 'network', name: 'network.caro.care', Icon: WifiIcon, desc: 'Netwerk' },
    { id: 'files', name: 'files.caro.care', Icon: FileTextIcon, desc: 'Opslag' },
    { id: 'portal', name: 'portal.caro.care', Icon: LayoutIcon, desc: 'Dashboard' },
  ];

  // Calculate positions in a circle around the center
  const getPosition = (index, total, radius) => {
    const angle = (index * 2 * Math.PI) / total - Math.PI / 2;
    return {
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
    };
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        opacity: isActive ? 1 : 0,
        pointerEvents: isActive ? 'auto' : 'none',
        transition: 'opacity 0.7s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <AnimatedBackground />

      <Box sx={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 1100, px: 4 }}>
        <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: 'white', textAlign: 'center', mb: 1 }}>
          CARO <span style={{ color: colors.plum.light }}>Datacenter</span>
        </Typography>
        <Typography sx={{ fontSize: '1.1rem', color: '#9ca3af', textAlign: 'center', mb: 3 }}>
          Alle services gehost op één centrale, redundante locatie
        </Typography>

        {/* Circular Layout with Datacenter in Center */}
        <Box sx={{ position: 'relative', width: '100%', height: 480, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

          {/* Connection lines from services to datacenter */}
          <svg
            width="100%"
            height="100%"
            viewBox="-400 -240 800 480"
            style={{ position: 'absolute', top: 0, left: 0 }}
          >
            <defs>
              <linearGradient id="serviceLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={colors.plum.light} stopOpacity="0.3" />
                <stop offset="100%" stopColor={colors.gold.main} stopOpacity="0.8" />
              </linearGradient>
            </defs>
            {services.map((service, i) => {
              const pos = getPosition(i, services.length, 180);
              return (
                <line
                  key={`line-${service.id}`}
                  x1="0"
                  y1="0"
                  x2={pos.x}
                  y2={pos.y}
                  stroke="url(#serviceLineGradient)"
                  strokeWidth="2"
                  strokeDasharray="6,4"
                  opacity={phase >= 2 ? 0.8 : 0}
                  style={{
                    transition: 'opacity 0.5s ease',
                    transitionDelay: `${i * 80}ms`,
                  }}
                />
              );
            })}
          </svg>

          {/* Central Datacenter */}
          <Box
            sx={{
              position: 'absolute',
              width: 160,
              height: 160,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${colors.plum.main}, ${colors.plum.dark})`,
              border: `3px solid ${colors.gold.main}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 60px ${colors.plum.main}60, 0 0 100px ${colors.gold.main}30`,
              opacity: phase >= 1 ? 1 : 0,
              transform: phase >= 1 ? 'scale(1)' : 'scale(0.5)',
              transition: 'all 0.6s ease',
              zIndex: 20,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <ServerIcon sx={{ width: 28, height: 28, color: 'white' }} />
              <ServerIcon sx={{ width: 28, height: 28, color: 'white' }} />
            </Box>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>DATACENTER</Typography>
            <Typography sx={{ color: colors.gold.light, fontSize: '0.7rem' }}>Redundant</Typography>
          </Box>

          {/* Services in a circle */}
          {services.map((service, i) => {
            const { Icon } = service;
            const pos = getPosition(i, services.length, 200);
            return (
              <Box
                key={service.id}
                sx={{
                  position: 'absolute',
                  left: `calc(50% + ${pos.x}px - 70px)`,
                  top: `calc(50% + ${pos.y}px - 45px)`,
                  width: 140,
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${colors.plum.dark}`,
                  background: `${colors.plum.darker}90`,
                  textAlign: 'center',
                  opacity: phase >= 2 ? 1 : 0,
                  transform: phase >= 2 ? 'scale(1)' : 'scale(0.8)',
                  transition: 'all 0.5s ease',
                  transitionDelay: `${i * 80 + 200}ms`,
                  zIndex: 10,
                }}
              >
                <Icon sx={{ width: 24, height: 24, color: colors.gold.main, mb: 0.5 }} />
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#d1d5db', mb: 0.25 }}>
                  {service.name}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#9ca3af' }}>
                  {service.desc}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Bottom info badges */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            mt: 2,
            opacity: phase >= 3 ? 1 : 0,
            transition: 'opacity 0.5s ease',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, bgcolor: 'rgba(34, 197, 94, 0.15)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
            <ShieldIcon sx={{ width: 18, height: 18, color: '#4ade80' }} />
            <Typography sx={{ color: '#4ade80', fontSize: '0.8rem', fontWeight: 500 }}>Redundante servers</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, bgcolor: 'rgba(34, 197, 94, 0.15)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
            <LockIcon sx={{ width: 18, height: 18, color: '#4ade80' }} />
            <Typography sx={{ color: '#4ade80', fontSize: '0.8rem', fontWeight: 500 }}>End-to-end encryptie</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, bgcolor: 'rgba(34, 197, 94, 0.15)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
            <ZapIcon sx={{ width: 18, height: 18, color: '#4ade80' }} />
            <Typography sx={{ color: '#4ade80', fontSize: '0.8rem', fontWeight: 500 }}>99.9% uptime</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, bgcolor: 'rgba(34, 197, 94, 0.15)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
            <ShieldIcon sx={{ width: 18, height: 18, color: '#4ade80' }} />
            <Typography sx={{ color: '#4ade80', fontSize: '0.8rem', fontWeight: 500 }}>Bij downtime is er wachtrij</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Section 4: One Stop Shopping - Integrations
const IntegrationsSection = ({ isActive }) => {
  const [showItems, setShowItems] = useState(false);

  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => setShowItems(true), 400);
      return () => clearTimeout(timer);
    }
    setShowItems(false);
  }, [isActive]);

  const integrations = [
      // Ready integrations
      { name: 'Apparaatbeheer (MDM)', desc: 'Centraal beheer van alle tablets en andere endpoints', status: 'ready', Icon: MonitorIcon },
      { name: 'VoIP Telefonie', desc: 'Telefooncentrale integratie per locatie met SIP-trunks naar externe providers', status: 'ready', Icon: PhoneIcon },
      { name: 'Videofonie & Intercom', desc: 'IP intercom en toegangscontrole', status: 'ready', Icon: CameraIcon },
      { name: 'Noodoproep Sturing', desc: 'Alarmroutering naar externe partners of mantelzorgers', status: 'ready', Icon: BellIcon },
      { name: 'Netwerk Beheer', desc: 'Firewall, Switches, AP\'s en gateways', status: 'ready', Icon: WifiIcon },
      { name: 'IoT Sensoren', desc: 'Draadloze en batterijloze knoppen en sensoren', status: 'ready', Icon: SensorIcon },
      { name: 'Domotica', desc: 'Smart home automatisering', status: 'ready', Icon: LightbulbIcon },
      // Coming soon
      { name: 'Alarm anywhere', desc: 'Mobiele alarmering zoals je gewend bent, maar ook buiten de muren van het gebouw', status: 'soon', Icon: MobileIcon },
      { name: 'Beveiligde Opslag', desc: 'Documenten en dossiers', status: 'soon', Icon: ShieldIcon },
      { name: 'Activiteitenplanning', desc: 'Dagprogramma en activiteitenbeheer', status: 'soon', Icon: CalendarIcon },
      { name: 'Communitybuilding', desc: 'Interne en externe communicatie met bewoners, familieleden en zorgverleners', status: 'soon', Icon: MessageCircleIcon },
      { name: 'Plaatsbeschrijvingen', desc: 'Inspectie en waarborg beheer', status: 'tbd', Icon: ShelterIcon },
      { name: 'Dwaaldetectie', desc: 'Locatiebepaling en waarschuwingen', status: 'soon', Icon: MapPinIcon },
      { name: 'Valdetectie', desc: 'Automatische valalarmering', status: 'soon', Icon: HeartbeatIcon },
      { name: 'Medicatiebeheer', desc: 'Herinneringen en opvolging', status: 'tbd', Icon: PillIcon },
      { name: 'Integratie met zorgplatforms van FOD', desc: 'Naadloze koppeling met overheidsdiensten', status: 'tbd', Icon: GovernmentIcon },
      { name: 'CRM & Financieel', desc: 'Boekhouding integratie', status: 'tbd', Icon: EuroIcon },
      { name: 'Personeelsplanning', desc: 'Roosters en bezetting', status: 'tbd', Icon: UsersIcon },
      { name: 'En nog zo veel meer', desc: 'We luisteren naar jullie noden...', status: '', Icon: IntegrationIcon },
    ];

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        opacity: isActive ? 1 : 0,
        pointerEvents: isActive ? 'auto' : 'none',
        transition: 'opacity 0.7s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <AnimatedBackground variant="gold" />

      <Box sx={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 1400, px: 4 }}>
        <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: 'white', textAlign: 'center', mb: 1 }}>
          One Stop <span style={{ color: colors.gold.main }}>Shopping</span>
        </Typography>
        <Typography sx={{ fontSize: '1.1rem', color: '#9ca3af', textAlign: 'center', mb: 4 }}>
          Alle integraties die klanten nodig hebben - van apparaatbeheer tot alarmbeheer
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 2, maxWidth: 1200, mx: 'auto', mb: 4 }}>
          {integrations.map((integration, i) => {
            const { Icon } = integration;
            const isReady = integration.status === 'ready';
            const isSoon = integration.status === 'soon';
            const isTbd = integration.status === 'tbd';

            const getStatusStyle = () => {
              if (isReady) return { bg: 'rgba(34, 197, 94, 0.2)', border: 'rgba(34, 197, 94, 0.5)', color: '#4ade80', label: 'READY' };
              if (isSoon) return { bg: 'rgba(234, 179, 8, 0.2)', border: 'rgba(234, 179, 8, 0.5)', color: '#facc15', label: 'SOON' };
              if (isTbd) return { bg: 'rgba(156, 163, 175, 0.2)', border: 'rgba(156, 163, 175, 0.5)', color: '#9ca3af', label: 'TBD' };
              return null;
            };
            const statusStyle = getStatusStyle();

            return (
              <Box
                key={integration.name}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: isReady ? `${colors.plum.darker}80` : 'rgba(75, 85, 99, 0.3)',
                  border: `1px solid ${isReady ? colors.plum.dark : 'rgba(75, 85, 99, 0.5)'}`,
                  textAlign: 'center',
                  opacity: showItems ? 1 : 0,
                  transform: showItems ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'all 0.4s ease',
                  transitionDelay: `${i * 50}ms`,
                  position: 'relative',
                }}
              >
                {statusStyle && (
                  <Box sx={{ position: 'absolute', top: 8, right: 8, px: 1, py: 0.25, borderRadius: 9999, bgcolor: statusStyle.bg, border: `1px solid ${statusStyle.border}` }}>
                    <Typography sx={{ fontSize: '0.6rem', color: statusStyle.color, fontWeight: 600 }}>
                      {statusStyle.label}
                    </Typography>
                  </Box>
                )}
                <Icon sx={{ width: 28, height: 28, color: isReady ? colors.gold.main : '#6b7280', mb: 1 }} />
                <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>{integration.name}</Typography>
                <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem' }}>{integration.desc}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

// Section 5: Portal Features (with clickable demos)
const PortalSection = ({ isActive }) => {
  const [showItems, setShowItems] = useState(false);

  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => setShowItems(true), 400);
      return () => clearTimeout(timer);
    }
    setShowItems(false);
  }, [isActive]);

  const features = [
    { Icon: PhoneIcon, name: 'Videofonie', demo: false },
    { Icon: MonitorIcon, name: 'Tablets', demo: true },
    { Icon: RadioIcon, name: 'IoT Knoppen', demo: true },
    { Icon: ZapIcon, name: 'MQTT & SSH Commands', demo: false },
    { Icon: CameraIcon, name: 'Camera Aanvraag (GDPR)', demo: false },
    { Icon: TicketIcon, name: 'Ticketing Systeem', demo: false },
    { Icon: BuildingIcon, name: 'Bedrijfsbeheer', demo: false },
    { Icon: UsersIcon, name: 'Gebruikersbeheer', demo: false },
    { Icon: BellIcon, name: 'Alarm Integratie', demo: false },
    { Icon: SettingsIcon, name: 'Apparaat Updates', demo: false },
    { Icon: MapPinIcon, name: 'Indoor Navigatie', demo: true },
    { Icon: MonitorIcon, name: 'Alarm Dashboard', demo: true },
  ];

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        opacity: isActive ? 1 : 0,
        pointerEvents: isActive ? 'auto' : 'none',
        transition: 'opacity 0.7s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <AnimatedBackground variant="gold" />

      <Box sx={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 1100, px: 4 }}>
        <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: 'white', textAlign: 'center', mb: 0.5 }}>
          <span style={{ color: colors.gold.main }}>portal</span>.caro.care
        </Typography>
        <Typography sx={{ fontSize: '1.1rem', color: '#9ca3af', textAlign: 'center', mb: 1.5 }}>
          Het Operating System voor Assistentiewoningen
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, borderRadius: 9999, bgcolor: `${colors.gold.main}30`, color: colors.gold.light, fontSize: '0.85rem' }}>
            <PlayIcon sx={{ width: 14, height: 14 }} />
            <span>Klik op items met</span>
            <OpenInNewIcon sx={{ width: 12, height: 12 }} />
            <span>voor demo</span>
          </Box>
        </Box>

        {/* Feature Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, maxWidth: 950, mx: 'auto' }}>
          {features.map((feature, i) => {
            const { Icon } = feature;
            return (
              <Box
                key={feature.name}
                onClick={feature.demo ? openDemo : undefined}
                sx={{
                  position: 'relative',
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${feature.demo ? colors.gold.main + '60' : colors.plum.dark}`,
                  background: feature.demo
                    ? `linear-gradient(135deg, ${colors.gold.darker}50, ${colors.gold.dark}30)`
                    : `${colors.plum.darker}60`,
                  opacity: showItems ? 1 : 0,
                  transform: showItems ? 'translateY(0)' : 'translateY(16px)',
                  transition: 'all 0.4s ease',
                  transitionDelay: `${i * 40}ms`,
                  cursor: feature.demo ? 'pointer' : 'default',
                  '&:hover': feature.demo ? {
                    transform: 'translateY(-4px)',
                    borderColor: colors.gold.main,
                    boxShadow: `0 8px 24px ${colors.gold.main}30`,
                  } : {},
                }}
              >
                {feature.demo && (
                  <Box sx={{ position: 'absolute', top: -8, right: -8, width: 22, height: 22, borderRadius: '50%', bgcolor: colors.gold.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <OpenInNewIcon sx={{ width: 12, height: 12, color: 'black' }} />
                  </Box>
                )}
                <Icon sx={{ width: 28, height: 28, mb: 1, color: feature.demo ? colors.gold.light : colors.plum.light }} />
                <Typography sx={{ fontSize: '0.85rem', color: 'white', fontWeight: 500 }}>{feature.name}</Typography>
              </Box>
            );
          })}
        </Box>

        {/* Integration Notes */}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Box sx={{ px: 2, py: 1, borderRadius: 2, border: `1px solid ${colors.plum.dark}`, bgcolor: `${colors.plum.darker}50`, display: 'flex', alignItems: 'center', gap: 1 }}>
            <HeartIcon sx={{ width: 18, height: 18, color: colors.plum.light }} />
            <Typography sx={{ color: '#d1d5db', fontSize: '0.85rem' }}>Mantelzorg integratie</Typography>
          </Box>
          <Box sx={{ px: 2, py: 1, borderRadius: 2, border: `1px solid ${colors.gold.dark}`, bgcolor: `${colors.gold.darker}50`, display: 'flex', alignItems: 'center', gap: 1 }}>
            <HeartIcon sx={{ width: 18, height: 18, color: colors.gold.light }} />
            <Typography sx={{ color: '#d1d5db', fontSize: '0.85rem' }}>Noodcentrale integratie</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Section 5: Roadmap 2026
const RoadmapSection = ({ isActive }) => {
  const [showItems, setShowItems] = useState(false);

  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(() => setShowItems(true), 400);
      return () => clearTimeout(timer);
    }
    setShowItems(false);
  }, [isActive]);

  const products = [
    {
      id: 'buddy',
      name: 'CARO Buddy',
      Icon: HomeIcon,
      target: 'Residentieel',
      desc: 'Lichtgewicht PAS systeem voor thuisgebruik',
      color: colors.gold.main,
    },
    {
      id: 'integrated',
      name: 'CARO Integrated PAS',
      Icon: BriefcaseIcon,
      target: 'B2B',
      desc: 'Projecten en groepen van woningen voor partners',
      color: colors.plum.light,
    },
    {
      id: 'os',
      name: 'CARO OS',
      Icon: BuildingAltIcon,
      target: 'B2B Enterprise',
      desc: 'Volledig platform voor uitbaters assistentiewoningen en zorgcentra',
      color: colors.plum.main,
    },
  ];

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        opacity: isActive ? 1 : 0,
        pointerEvents: isActive ? 'auto' : 'none',
        transition: 'opacity 0.7s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <AnimatedBackground />

      <Box sx={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 1100, px: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1 }}>
          <TargetIcon sx={{ width: 36, height: 36, color: colors.gold.main }} />
          <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: 'white' }}>
            Roadmap <span style={{ color: colors.gold.main }}>2026</span>
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '1.1rem', color: '#9ca3af', textAlign: 'center', mb: 5 }}>
          Go to Market met 3 Oplossingen
        </Typography>

        {/* Products */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 5 }}>
          {products.map((product, i) => {
            const { Icon } = product;
            return (
              <Box
                key={product.id}
                sx={{
                  width: 270,
                  p: 3,
                  borderRadius: 3,
                  border: `1px solid ${colors.plum.dark}`,
                  background: `linear-gradient(135deg, ${colors.plum.darker}80, ${colors.plum.dark}40)`,
                  opacity: showItems ? 1 : 0,
                  transform: showItems ? 'translateY(0)' : 'translateY(24px)',
                  transition: 'all 0.5s ease',
                  transitionDelay: `${i * 120}ms`,
                }}
              >
                <Box sx={{ width: 56, height: 56, borderRadius: 2, bgcolor: `${product.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <Icon sx={{ width: 28, height: 28, color: product.color }} />
                </Box>
                <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: 'white', mb: 0.25 }}>{product.name}</Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: product.color, mb: 1 }}>{product.target}</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#9ca3af' }}>{product.desc}</Typography>
              </Box>
            );
          })}
        </Box>

        {/* Supporting Elements */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2.5 }}>
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              borderRadius: 2,
              border: `1px solid ${colors.plum.dark}`,
              bgcolor: `${colors.plum.darker}60`,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              opacity: showItems ? 1 : 0,
              transition: 'opacity 0.5s ease',
              transitionDelay: '400ms',
            }}
          >
            <SmartphoneIcon sx={{ width: 28, height: 28, color: colors.gold.main }} />
            <Box>
              <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem' }}>Nieuwe App</Typography>
              <Typography sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>Ondersteunt alle 3 oplossingen</Typography>
            </Box>
          </Box>

          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              borderRadius: 2,
              border: `1px solid ${colors.plum.dark}`,
              bgcolor: `${colors.plum.darker}60`,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              opacity: showItems ? 1 : 0,
              transition: 'opacity 0.5s ease',
              transitionDelay: '500ms',
            }}
          >
            <SettingsIcon sx={{ width: 28, height: 28, color: colors.plum.light }} />
            <Box>
              <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem' }}>Optionele Features</Typography>
              <Typography sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>Per systeem & per apparaat configureerbaar</Typography>
            </Box>
          </Box>
        </Box>

        {/* Example */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', opacity: showItems ? 1 : 0, transition: 'opacity 0.5s ease', transitionDelay: '600ms' }}>
          <Box sx={{ px: 2, py: 1, borderRadius: 2, bgcolor: `${colors.gold.darker}30` }}>
            <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem' }}>
              Voorbeeld: Audio-spraak apparaten in appartementen zonder domotica
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Section 6: Conclusion
const ConclusionSection = ({ isActive }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (isActive) {
      setPhase(0);
      const timers = [
        setTimeout(() => setPhase(1), 300),
        setTimeout(() => setPhase(2), 900),
        setTimeout(() => setPhase(3), 1500),
        setTimeout(() => setPhase(4), 2100),
      ];
      return () => timers.forEach(t => clearTimeout(t));
    }
    setPhase(0);
  }, [isActive]);

  const stats = [
    { value: '7', label: '*.caro.care services' },
    { value: '12+', label: 'Portal integraties' },
    { value: '3', label: 'Go-to-market producten' },
    { value: '2', label: 'Redundante servers' },
  ];

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        opacity: isActive ? 1 : 0,
        pointerEvents: isActive ? 'auto' : 'none',
        transition: 'opacity 0.7s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <AnimatedBackground variant="success" />

      <Box sx={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 1000, px: 4 }}>
        {/* Timeline */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, mb: 6 }}>
          <Box sx={{ textAlign: 'center', opacity: phase >= 1 ? 1 : 0, transform: phase >= 1 ? 'translateY(0)' : 'translateY(24px)', transition: 'all 0.6s ease' }}>
            <Typography sx={{ fontSize: '4rem', fontWeight: 700, color: 'white', lineHeight: 1 }}>10</Typography>
            <Typography sx={{ fontSize: '1.1rem', color: '#9ca3af' }}>jaar</Typography>
            <Typography sx={{ color: '#6b7280', fontSize: '0.85rem', mt: 0.5 }}>ontwikkeling</Typography>
          </Box>

          <ArrowRightIcon sx={{ width: 40, height: 40, color: colors.plum.light, opacity: phase >= 2 ? 1 : 0, transition: 'opacity 0.5s ease' }} />

          <Box sx={{ textAlign: 'center', opacity: phase >= 2 ? 1 : 0, transform: phase >= 2 ? 'translateY(0)' : 'translateY(24px)', transition: 'all 0.6s ease' }}>
            <Typography sx={{ fontSize: '4rem', fontWeight: 700, color: colors.gold.main, lineHeight: 1 }}>1</Typography>
            <Typography sx={{ fontSize: '1.1rem', color: '#9ca3af' }}>jaar</Typography>
            <Typography sx={{ color: '#6b7280', fontSize: '0.85rem', mt: 0.5 }}>versie 3.0</Typography>
          </Box>

          <ArrowRightIcon sx={{ width: 40, height: 40, color: colors.gold.main, opacity: phase >= 3 ? 1 : 0, transition: 'opacity 0.5s ease' }} />

          <Box sx={{ textAlign: 'center', opacity: phase >= 3 ? 1 : 0, transform: phase >= 3 ? 'translateY(0)' : 'translateY(24px)', transition: 'all 0.6s ease' }}>
            <RocketIcon sx={{ width: 56, height: 56, color: colors.plum.light, mb: 0.5 }} />
            <Typography sx={{ fontSize: '1.1rem', color: 'white', fontWeight: 700 }}>GO TO</Typography>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: colors.gold.main }}>MARKET</Typography>
          </Box>
        </Box>

        {/* Final Message */}
        {phase >= 4 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5 }}>
            <Box
              sx={{
                px: 4,
                py: 2,
                borderRadius: 3,
                border: `2px solid ${colors.gold.main}`,
                background: `linear-gradient(135deg, ${colors.plum.main}40, ${colors.gold.dark}40)`,
                animation: 'finalMessagePop 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
                '@keyframes finalMessagePop': {
                  '0%': {
                    opacity: 0,
                    transform: 'scale(0.5) translateY(20px)',
                    boxShadow: `0 0 0px ${colors.gold.main}00`,
                  },
                  '60%': {
                    opacity: 1,
                    transform: 'scale(1.05) translateY(0)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'scale(1) translateY(0)',
                    boxShadow: `0 0 30px ${colors.gold.main}50, 0 0 60px ${colors.plum.main}30`,
                  },
                },
              }}
            >
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: 'white' }}>
                We zijn <span style={{ color: colors.gold.main }}>klaar</span> voor 2026!
              </Typography>
            </Box>
          </Box>
        )}

        {/* Stats */}
        {phase >= 4 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 5 }}>
            {stats.map((stat, i) => (
              <Box
                key={stat.label}
                sx={{
                  textAlign: 'center',
                  opacity: 0,
                  animation: 'fadeInUp 0.5s ease forwards',
                  animationDelay: `${i * 120 + 200}ms`,
                }}
              >
                <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: colors.plum.light }}>{stat.value}</Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>{stat.label}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Main Component
const BusinessUpdate = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const [currentSection, setCurrentSection] = useState(0);

  const isAdmin = hasPermission('API-management-portal', 'users.permissions.manage');

  const sections = [
    OldSystemSection,
    TransformationSection,
    EcosystemSection,
    IntegrationsSection,
    PortalSection,
    RoadmapSection,
    ConclusionSection,
  ];

  const sectionTitles = [
    'Oud',
    'Nieuw',
    'Ecosysteem',
    'Integraties',
    'Portal',
    'Roadmap',
    'Conclusie',
  ];

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        setCurrentSection((prev) => Math.min(prev + 1, sections.length - 1));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentSection((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Escape') {
        navigate(-1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, sections.length]);

  // Permission check
  if (!isAdmin) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#111827' }}>
        <Typography sx={{ color: '#9ca3af' }}>Je hebt geen toegang tot deze pagina.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        bgcolor: 'black',
        fontFamily: 'Inter, system-ui, sans-serif',
        '@keyframes bgPulse': {
          '0%, 100%': { opacity: 0.2 },
          '50%': { opacity: 0.3 },
        },
        '@keyframes dangerPulse': {
          '0%, 100%': { boxShadow: '0 0 20px rgba(239, 68, 68, 0.3)' },
          '50%': { boxShadow: '0 0 40px rgba(239, 68, 68, 0.5)' },
        },
        '@keyframes fadeInUp': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* Sections */}
      {sections.map((Section, i) => (
        <Section key={i} isActive={currentSection === i} />
      ))}

      {/* Navigation */}
      <SectionNav
        currentSection={currentSection}
        totalSections={sections.length}
        onNavigate={setCurrentSection}
        sectionTitles={sectionTitles}
      />

      {/* Keyboard hint */}
      <Box sx={{ position: 'fixed', bottom: 32, right: 32, color: '#4b5563', fontSize: '0.7rem' }}>
        ← → of spatiebalk | ESC om af te sluiten
      </Box>
    </Box>
  );
};

export default BusinessUpdate;
